import React, { useEffect, useState } from 'react'
import appBridge from 'libs/appBridge'
import styled from 'styled-components'
import store from 'modules'
import { Grid, Input, Breadcrumb } from '@shopline/dashboard-ui'
import useLoading from 'hooks/useLoading'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import * as productActionCreators from 'modules/products/actionCreator'
import { bindActionCreators } from 'redux'
import { getName, getImageFileExtension, transferThumbnailURL } from 'utils'
import DashboardLayout from 'layouts/DashboardLayout'
import { useParams, useHistory } from 'react-router-dom'
import { Button, LayoutDiv, Loading, BasicTable, EmptyString } from 'components'
import VariationEditPopup from 'components/VariationEditPopup'

import styles from 'scss/Variations.module.scss'

const StyledSearchSection = styled.section`
  padding: 8px;
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
`

const StyledSearchInputGroup = styled.div`
  display: flex;
  align-items: flex-start;

  button {
    margin-left: 6px;
  }
`

const StyledProductName = styled.p`
  font-size: 18px;
  color: #7a8499;
  margin: 0;
`

const Variations = () => {
  const dispatch = useDispatch()
  const { productId } = useParams()
  const history = useHistory()
  const [isLoading, openLoading, closeLoading] = useLoading()
  const { t, i18n } = useTranslation()
  const [filteredKeyword, setFilteredKeyword] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false)
  const { selectedProduct, variationImages } = useSelector((reduxState) => reduxState.products)

  const handleOpenPopup = () => {
    setIsEditPopupOpen(true)
    document.body.style.overflow = 'hidden'
  }

  const handleClosePopup = () => {
    setIsEditPopupOpen(false)
    document.body.style.overflow = 'visible'
  }

  const handleVariationSearch = (e) => {
    setFilteredKeyword(searchKeyword)
  }

  const { selectVariation, fetchGetVariationImages, fetchGetSingleProduct } = bindActionCreators(
    productActionCreators,
    dispatch,
  )

  const handleSelectVariation = (id) => {
    selectVariation({ id })
  }

  // api
  const handleFetchVariationImage = async () => {
    openLoading()
    try {
      await fetchGetVariationImages()
      closeLoading()
    } catch (errMsg) {
      console.error(errMsg)
      closeLoading()
    }
    handleOpenPopup()
  }

  // init
  useEffect(() => {
    const initFlow = async () => {
      if (!selectedProduct) {
        openLoading()
        try {
          await fetchGetSingleProduct({ productId })
          closeLoading()
        } catch (errMsg) {
          console.error(errMsg)
          closeLoading()
        }
      }
    }

    initFlow()
  }, [])

  return (
    <DashboardLayout>
      {isLoading ? <Loading /> : null}
      {selectedProduct ? (
        <div>
          <LayoutDiv className="mb-3">
            <Breadcrumb
              items={[
                {
                  id: 'apps',
                  label: t('apps'),
                },
                {
                  id: 'myApps',
                  label: t('myApps'),
                  url: '/apps/my_apps',
                  onClick: async () => {
                    appBridge.client.redirect('/apps/my_apps')
                  },
                },
                {
                  id: 'multiVariation',
                  label: t('appName'),
                  url: '/products',
                  onClick: () => {
                    history.push('/products')
                  },
                },
                {
                  id: 'variation',
                  label: t('variationPopupTitle'),
                },
              ]}
            />
          </LayoutDiv>
          <LayoutDiv>
            <h2>{t('variationPopupTitle')}</h2>
            <StyledProductName>
              {getName({ obj: selectedProduct.title_translations, i18n: i18n.language })}
            </StyledProductName>
          </LayoutDiv>

          <StyledSearchSection>
            <StyledSearchInputGroup>
              <Input
                style={{ width: '400px' }}
                value={searchKeyword}
                type="searchProduct"
                placeholder={t('variationSearchPlace')}
                onChange={(e) => setSearchKeyword(e.target.value)}
                status="valid"
                block
              />
              <Button color="primary" onClick={handleVariationSearch}>
                {t('search')}
              </Button>
            </StyledSearchInputGroup>
          </StyledSearchSection>

          <Grid.Column lg={12} className={styles['variations-table']}>
            <BasicTable
              isLoading={false}
              columns={[
                { key: 'variation', title: t('variant'), width: '20%' },
                { key: 'sku', title: t('sku'), width: '20%' },
                { key: 'relatedPhotos', title: t('relatedPhotos'), width: '40%' },
                { key: 'action', title: '', width: '15%' },
              ]}
              dataSrc={selectedProduct?.variations
                .filter((item) => {
                  if (item.sku && item.sku.includes(filteredKeyword)) {
                    return true
                  }

                  for (const key in item.fields_translations) {
                    if (item.fields_translations[key].includes(filteredKeyword)) {
                      return true
                    }
                  }

                  return false
                })
                .map(
                  (variation) =>
                    ({
                      sku: variation.sku || <EmptyString />,
                      variation: getName({ obj: variation.fields_translations, i18n: i18n.language }),
                      relatedPhotos: variation.selected_images.slice(0, 5).map((photoObj, index) => (
                        <div
                          key={photoObj._id}
                          data-isoverlimit={variation.selected_images.length > 5}
                          data-remainingimages={`+ ${variation.selected_images.length - 5}`}
                          className={styles['variation-image']}
                          onClick={() => {
                            if (variation.selected_images.length > 5 && index === 4) {
                              handleSelectVariation(variation._id)
                              handleFetchVariationImage(variation._id)
                            }
                          }}
                        >
                          <img
                            src={transferThumbnailURL(
                              photoObj._id,
                              store.getState().merchantId,
                              getImageFileExtension(photoObj.url),
                            )}
                          />
                        </div>
                      )),
                      action: (
                        <Button
                          color="primary"
                          onClick={() => {
                            handleSelectVariation(variation._id)
                            handleFetchVariationImage(variation._id)
                          }}
                        >
                          {t('selectImage')}
                        </Button>
                      ),
                      id: variation._id,
                    } || []),
                )}
            />
          </Grid.Column>
        </div>
      ) : null}
      {isEditPopupOpen ? <VariationEditPopup closePopup={handleClosePopup} /> : null}
    </DashboardLayout>
  )
}
export default Variations
